// Globals
import { TFunction } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';

// Components
import { StyledBadge } from './productRegionsAndPricesListStyles';
import { InputNumber, Radio, RadioGroup } from '@/ui';

// Models
import { IBulkUpdateStockItem, IProductRegionsAndPricesListItem } from '@/models/products.model';

export const ALL_REGIONS_ROW_ID = 'all';

type TGetProductsTableColumns = {
  t: TFunction;
  isEditing: boolean;
  editedData: Map<string, IBulkUpdateStockItem>;
  onFieldChange: (id: string, field: keyof IBulkUpdateStockItem, value: boolean | number) => void;
  allRegionsPrice: number | null;
  allRegionsDiscountPrice: number | null;
  onAllRegionsFieldChange: (field: 'price' | 'discount_price', value: number | null) => void;
};

export const getProductsTableColumns = ({
  t,
  isEditing,
  editedData,
  onFieldChange,
  allRegionsPrice,
  allRegionsDiscountPrice,
  onAllRegionsFieldChange,
}: TGetProductsTableColumns): ColumnsType<IProductRegionsAndPricesListItem> => {
  const getEditedValue = <K extends keyof IBulkUpdateStockItem>(
    record: IProductRegionsAndPricesListItem,
    field: K,
  ): IBulkUpdateStockItem[K] | IProductRegionsAndPricesListItem[K] => {
    const edited = editedData.get(record.id);
    if (edited && field in edited) {
      return edited[field] as IBulkUpdateStockItem[K];
    }
    return record[field];
  };

  return [
    {
      title: t('regions_and_price_table_col_region'),
      dataIndex: 'region',
      key: 'region',
      render: (region, record) => {
        if (record.id === ALL_REGIONS_ROW_ID) {
          return <strong>{t('all_regions_row_label')}</strong>;
        }
        return region;
      },
    },
    {
      title: t('regions_and_price_table_col_available'),
      dataIndex: 'available',
      key: 'available',
      render: (available, record) => {
        if (record.id === ALL_REGIONS_ROW_ID) {
          return '–';
        }
        if (isEditing) {
          const currentValue = getEditedValue(record, 'available') as boolean;
          return (
            <RadioGroup value={currentValue} onChange={(e) => onFieldChange(record.id, 'available', e.target.value)}>
              <Radio value={true}>{t('regions_and_prices_yes_title')}</Radio>
              <Radio value={false}>{t('regions_and_prices_non_title')}</Radio>
            </RadioGroup>
          );
        }
        return (
          <StyledBadge $isActive={available}>
            {available ? t('regions_and_prices_yes_title') : t('regions_and_prices_non_title')}
          </StyledBadge>
        );
      },
    },
    {
      title: t('regions_and_price_table_col_quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity, record) => {
        if (record.id === ALL_REGIONS_ROW_ID) {
          return '–';
        }
        return quantity;
      },
    },
    {
      title: t('regions_and_price_table_col_price'),
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => {
        if (record.id === ALL_REGIONS_ROW_ID) {
          return (
            <InputNumber
              value={allRegionsPrice ?? undefined}
              min={0}
              onChange={(value) => onAllRegionsFieldChange('price', value as number | null)}
            />
          );
        }
        if (isEditing) {
          const currentValue = getEditedValue(record, 'price');
          return (
            <InputNumber
              value={Number(currentValue)}
              min={0}
              onChange={(value) => onFieldChange(record.id, 'price', value as number)}
            />
          );
        }
        return price;
      },
    },
    {
      title: t('regions_and_price_table_col_discount_price'),
      dataIndex: 'discount_price',
      key: 'discount_price',
      render: (discount_price, record) => {
        if (record.id === ALL_REGIONS_ROW_ID) {
          return (
            <InputNumber
              value={allRegionsDiscountPrice ?? undefined}
              min={0}
              onChange={(value) => onAllRegionsFieldChange('discount_price', value as number | null)}
            />
          );
        }
        if (isEditing) {
          const currentValue = getEditedValue(record, 'discount_price');
          return (
            <InputNumber
              value={currentValue ? Number(currentValue) : undefined}
              min={0}
              onChange={(value) => onFieldChange(record.id, 'discount_price', value as number)}
            />
          );
        }
        return discount_price;
      },
    },
  ];
};
